import { useTranslation } from 'react-i18next';
import { useState, useRef, useEffect } from 'react';
import { Bell } from 'lucide-react';
import { useNotifications } from '../../contexts/NotificationContext';
import { NotificationList } from './NotificationList';

export const NotificationBell = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const { unreadCount } = useNotifications();
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative w-9 h-9 flex items-center justify-center text-neutral-600 hover:text-neutral-800 focus:outline-none"
        aria-label={
          unreadCount > 0
            ? t('notifications.notificationBell.unread', { count: unreadCount })
            : t('notifications.notificationBell.notifications')
        }
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span
            aria-hidden
            className="absolute -top-0.5 -end-0.5 inline-flex min-w-4 items-center justify-center rounded-full border-2 border-white bg-danger-500 px-1 text-micro font-semibold leading-none tabular-nums text-white"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          {/* Mobile: fixed full-width panel pinned below topbar */}
          <div className="sm:hidden fixed inset-x-0 top-[112px] z-50 px-3">
            <div className="bg-white rounded-2xl shadow-[0_12px_32px_rgba(11,42,91,.14),0_4px_8px_rgba(11,42,91,.08)] border border-neutral-200 overflow-hidden">
              <NotificationList onClose={() => setIsOpen(false)} />
            </div>
          </div>

          {/* Desktop: regular dropdown aligned to the right */}
          <div
            className="hidden sm:block absolute end-0 mt-2 w-80 bg-white rounded-xl border border-neutral-200 z-50
            shadow-[0_12px_32px_rgba(11,42,91,.12),0_4px_8px_rgba(11,42,91,.06)] overflow-hidden"
          >
            <NotificationList onClose={() => setIsOpen(false)} />
          </div>
        </>
      )}
    </div>
  );
};
